import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight,
  House,
  UsersThree,
  Train,
  HouseLine,
  Lightning,
  CheckCircle,
  Clock,
  CurrencyEur,
  MapPin,
  Gauge,
  GasPump,
  CalendarBlank,
  DownloadSimple,
  ArrowsClockwise,
  Briefcase,
} from '@phosphor-icons/react';
import {
  computeResults,
  computeJobSwitch,
  FUEL_DEFAULTS,
  PRESETS,
  formatEUR,
  formatNumber,
} from '../lib/pendelLogic';
import { TrustBadges } from './TrustBadges';
import ShareResult from './ShareResult';
import BenchmarkPanel from './BenchmarkPanel';
import CO2Panel from './CO2Panel';
import BuyMeCoffee from './BuyMeCoffee';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ICON_MAP = {
  house: House,
  'users-three': UsersThree,
  train: Train,
  'house-line': HouseLine,
  lightning: Lightning,
  'check-circle': CheckCircle,
};

const DEFAULT_INPUT = {
  distance_km: 25,
  office_days: 4,
  minutes_one_way: 35,
  fuel_type: 'diesel',
  fuel_price: FUEL_DEFAULTS.diesel,
  consumption: 6.5,
  plz: '',
};

export default function Calculator({ initialPreset = 'mittel', showJobSwitch = false, onInputChange }) {
  const [input, setInput] = useState(() => {
    const base = {
      ...DEFAULT_INPUT,
      ...(PRESETS[initialPreset] || {}),
      fuel_price: FUEL_DEFAULTS[(PRESETS[initialPreset] || DEFAULT_INPUT).fuel_type],
    };
    // Read shared-link URL params on first load (browser only)
    if (typeof window !== 'undefined') {
      const sp = new URLSearchParams(window.location.search);
      const d = sp.get('d');
      const o = sp.get('o');
      const m = sp.get('m');
      if (d) base.distance_km = Math.max(0, Math.min(500, Number(d) || base.distance_km));
      if (o) base.office_days = Math.max(0, Math.min(7, Number(o) || base.office_days));
      if (m) base.minutes_one_way = Math.max(1, Math.min(300, Number(m) || base.minutes_one_way));
    }
    return base;
  });
  const [fuelLoading, setFuelLoading] = useState(false);
  const [fuelSource, setFuelSource] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [emailForLead, setEmailForLead] = useState('');
  const [leadStatus, setLeadStatus] = useState(null);

  const results = useMemo(() => computeResults(input), [input]);

  // Notify parent (SeoPage) about input changes so FocusPanel stays in sync.
  useEffect(() => {
    if (typeof onInputChange === 'function') onInputChange(input);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input]);

  // Record one anonymous "engaged user" event 10s after mount (no PII).
  useEffect(() => {
    const t = setTimeout(() => {
      try {
        axios
          .post(`${API}/calc-events`, {
            score: results.score,
            distance_km: results.distance_km,
          })
          .catch(() => {});
      } catch (_) {}
    }, 10000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function update(field, value) {
    setInput((prev) => ({ ...prev, [field]: value }));
  }

  function applyPreset(key) {
    const p = PRESETS[key];
    setInput((prev) => ({
      ...prev,
      ...p,
      fuel_price: FUEL_DEFAULTS[p.fuel_type],
    }));
  }

  async function fetchFuelPrice(useGeo = false) {
    setFuelLoading(true);
    setFuelSource(null);
    try {
      let params = { fuel: input.fuel_type };
      if (useGeo) {
        const coords = await new Promise((resolve, reject) => {
          if (!navigator.geolocation) return reject(new Error('Geolocation nicht verfügbar'));
          navigator.geolocation.getCurrentPosition(
            (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
            (err) => reject(err),
            { timeout: 8000, maximumAge: 600000 }
          );
        });
        params.lat = coords.lat.toFixed(4);
        params.lng = coords.lng.toFixed(4);
      } else {
        if (!input.plz || input.plz.length !== 5) {
          setFuelSource('Bitte gültige 5-stellige PLZ eingeben oder Standort nutzen');
          setFuelLoading(false);
          return;
        }
        params.plz = input.plz;
      }
      const r = await axios.get(`${API}/fuel-prices`, { params });
      update('fuel_price', r.data.price);
      let src;
      if (r.data.source === 'tankerkoenig_live') {
        src = `🟢 LIVE-Preis aus ${r.data.stations_count} Tankstellen${useGeo ? ' (Standort)' : ` (PLZ ${input.plz})`}`;
      } else {
        src = '🟡 Bundesweiter Durchschnittspreis · für Live-Daten Tankerkönig-API-Key benötigt';
      }
      setFuelSource(src);
    } catch (e) {
      if (useGeo) {
        setFuelSource('Standort konnte nicht ermittelt werden — bitte PLZ eingeben');
      } else {
        setFuelSource('Spritpreis konnte nicht geladen werden — bitte manuell setzen');
      }
    } finally {
      setFuelLoading(false);
    }
  }

  // Auto-update default fuel price when fuel type changes (only if untouched)
  useEffect(() => {
    setInput((prev) => ({ ...prev, fuel_price: FUEL_DEFAULTS[prev.fuel_type] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input.fuel_type]);

  async function downloadPdf() {
    setPdfLoading(true);
    try {
      const payload = {
        distance_km: results.distance_km,
        office_days: results.office_days,
        fuel_price: results.fuel_price,
        consumption: results.consumption,
        monthly_cost: results.monthly_cost,
        annual_cost: results.annual_cost,
        annual_hours: results.annual_hours,
        annual_days: results.annual_days,
        score: results.score,
        score_label: results.score_label,
        recommendations: results.recommendations.map((r) => `${r.title}: ${r.detail}`),
        comparisons: results.comparisons,
        email: emailForLead || undefined,
      };
      const r = await axios.post(`${API}/export-pdf`, payload, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'pendelpilot-report.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      if (emailForLead) setLeadStatus('Vielen Dank — dein Report ist unterwegs.');
    } catch (e) {
      setLeadStatus('PDF-Erstellung fehlgeschlagen.');
    } finally {
      setPdfLoading(false);
    }
  }

  return (
    <div className="w-full" data-testid="calculator-root">
      {/* PRESET QUICK START */}
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-neutral-500 mb-3">Schnellstart</div>
        <div className="grid grid-cols-3 gap-2" data-testid="preset-row">
          {Object.entries(PRESETS).map(([key, p]) => (
            <button
              key={key}
              data-testid={`preset-${key}`}
              onClick={() => applyPreset(key)}
              className="grid-border h-14 px-3 text-sm hover:bg-neutral-900 hover:text-white transition-colors duration-150"
            >
              <div className="flex flex-col items-start justify-center">
                <span className="font-mono text-xs opacity-70">{p.distance_km} km</span>
                <span className="font-semibold">{p.label}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* INPUT GRID */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 grid-border">
        <Field
          label="Pendelstrecke (einfach)"
          icon={<MapPin size={18} />}
          right="km"
          testId="distance-input"
          value={input.distance_km}
          onChange={(v) => update('distance_km', v)}
          type="number"
          min={0}
        />
        <Field
          label="Bürotage pro Woche"
          icon={<CalendarBlank size={18} />}
          right="Tage"
          testId="office-days-input"
          value={input.office_days}
          onChange={(v) => update('office_days', v)}
          type="number"
          min={0}
          max={7}
        />
        <Field
          label="Fahrzeit (einfach)"
          icon={<Clock size={18} />}
          right="Min."
          testId="minutes-input"
          value={input.minutes_one_way}
          onChange={(v) => update('minutes_one_way', v)}
          type="number"
          min={1}
        />
        <Field
          label="Verbrauch"
          icon={<Gauge size={18} />}
          right="L/100km"
          testId="consumption-input"
          value={input.consumption}
          onChange={(v) => update('consumption', v)}
          type="number"
          step={0.1}
          min={0}
        />

        {/* Fuel block spans two */}
        <div className="lg:col-span-2 border-t border-neutral-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <label className="text-xs uppercase tracking-widest text-neutral-500">Spritpreis-Automatik</label>
            <div className="inline-flex border border-neutral-200" data-testid="fuel-type-tabs">
              {['diesel', 'e5', 'e10'].map((ft) => (
                <button
                  key={ft}
                  data-testid={`fuel-${ft}`}
                  onClick={() => update('fuel_type', ft)}
                  className={`px-3 h-9 text-xs uppercase tracking-widest transition-colors ${
                    input.fuel_type === ft ? 'bg-neutral-900 text-white' : 'bg-white text-neutral-700 hover:bg-neutral-100'
                  }`}
                >
                  {ft === 'diesel' ? 'Diesel' : ft.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-end">
            <div className="sm:col-span-3">
              <label className="block text-xs text-neutral-500 mb-1">PLZ</label>
              <input
                data-testid="plz-input"
                value={input.plz}
                onChange={(e) => update('plz', e.target.value.replace(/\D/g, '').slice(0, 5))}
                placeholder="z.B. 80331"
                inputMode="numeric"
                className="w-full h-12 px-3 border border-neutral-300 bg-white font-mono"
              />
            </div>
            <div className="sm:col-span-3">
              <button
                data-testid="fuel-fetch-button"
                onClick={() => fetchFuelPrice(false)}
                disabled={fuelLoading}
                className="w-full h-12 bg-neutral-900 text-white text-xs uppercase tracking-widest hover:bg-black disabled:opacity-50 inline-flex items-center justify-center gap-2"
              >
                <ArrowsClockwise size={16} className={fuelLoading ? 'animate-spin' : ''} />
                Preis per PLZ
              </button>
            </div>
            <div className="sm:col-span-2">
              <button
                data-testid="fuel-geo-button"
                onClick={() => fetchFuelPrice(true)}
                disabled={fuelLoading}
                title="Aktuelle Spritpreise rund um deinen Standort"
                className="w-full h-12 border border-neutral-900 text-neutral-900 text-xs uppercase tracking-widest hover:bg-neutral-900 hover:text-white disabled:opacity-50 inline-flex items-center justify-center gap-2"
              >
                <MapPin size={16} weight="bold" />
                Standort
              </button>
            </div>
            <div className="sm:col-span-4">
              <label className="block text-xs text-neutral-500 mb-1">Preis €/Liter</label>
              <div className="flex items-center border border-neutral-300 bg-white h-12">
                <GasPump size={18} className="ml-3 text-neutral-500" />
                <input
                  data-testid="fuel-price-input"
                  type="number"
                  step="0.001"
                  value={input.fuel_price}
                  onChange={(e) => update('fuel_price', e.target.value)}
                  className="w-full h-full px-3 bg-transparent font-mono"
                />
              </div>
            </div>
          </div>
          {fuelSource && (
            <div className="mt-2 text-xs text-neutral-500" data-testid="fuel-source">
              {fuelSource}
            </div>
          )}
        </div>
      </div>

      {/* RESULTS */}
      <Results results={results} />

      {/* JOB SWITCH (optional) */}
      {showJobSwitch && <JobSwitchPanel baseInputs={input} />}

      {/* PDF EXPORT */}
      <div className="mt-12 grid-border p-6 md:p-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
          <div>
            <h3 className="font-display text-2xl md:text-3xl">Dein Ergebnis als PDF</h3>
            <p className="text-sm text-neutral-600 mt-2 max-w-md">
              Sichere dir deinen persönlichen Pendel-Report — inklusive Score, Lebenszeit-Bilanz und allen Empfehlungen.
            </p>
          </div>
          <div className="space-y-3">
            <input
              data-testid="lead-email-input"
              type="email"
              placeholder="E-Mail (optional, für Updates)"
              value={emailForLead}
              onChange={(e) => setEmailForLead(e.target.value)}
              className="w-full h-12 px-4 border border-neutral-300 bg-white"
            />
            <button
              data-testid="download-pdf-button"
              onClick={downloadPdf}
              disabled={pdfLoading}
              className="w-full h-14 bg-neutral-900 text-white text-sm uppercase tracking-widest hover:bg-black disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              <DownloadSimple size={18} weight="bold" />
              {pdfLoading ? 'Erstelle PDF…' : 'PDF-Report herunterladen'}
            </button>
            {leadStatus && (
              <div className="text-xs text-neutral-600" data-testid="lead-status">{leadStatus}</div>
            )}
          </div>
        </div>
        <TrustBadges className="mt-6" />
      </div>
    </div>
  );
}

function Field({ label, icon, right, value, onChange, testId, ...rest }) {
  return (
    <div className="p-5 border-b border-neutral-200 lg:[&:nth-child(2n)]:border-l">
      <label className="text-xs uppercase tracking-widest text-neutral-500 flex items-center gap-2">
        <span className="text-neutral-700">{icon}</span>
        {label}
      </label>
      <div className="mt-2 flex items-baseline gap-2">
        <input
          {...rest}
          data-testid={testId}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full text-3xl font-display tracking-tight bg-transparent outline-none"
        />
        {right && <span className="font-mono text-sm text-neutral-500">{right}</span>}
      </div>
    </div>
  );
}

function Results({ results }) {
  const {
    score,
    score_label,
    score_color,
    score_explanation,
    monthly_cost,
    annual_cost,
    annual_hours,
    annual_days,
    annual_km,
    pauschale_year,
    recommendations,
    comparisons,
  } = results;

  return (
    <div className="mt-12" data-testid="results-root">
      {/* Score hero */}
      <motion.div
        key={score}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="grid grid-cols-1 lg:grid-cols-3 grid-border"
      >
        <div
          className="p-8 md:p-12 lg:col-span-1 flex flex-col justify-between"
          style={{ backgroundColor: score_color }}
          data-testid="pendel-score-hero"
        >
          <div className="text-white/80 text-xs uppercase tracking-widest">Dein Pendel-Score</div>
          <div className="text-white">
            <div className="font-display text-[24vw] sm:text-[14vw] lg:text-[10vw] leading-none tnum" data-testid="score-value">
              {score}
            </div>
            <div className="text-sm uppercase tracking-widest opacity-90">/ 100</div>
          </div>
          <div className="text-white font-semibold mt-6" data-testid="score-label">{score_label}</div>
        </div>
        <div className="p-6 md:p-10 lg:col-span-2 flex flex-col justify-between">
          <p className="text-neutral-700 leading-relaxed" data-testid="score-explanation">{score_explanation}</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
            <Metric label="pro Monat" value={formatEUR(monthly_cost)} testId="metric-monthly" />
            <Metric label="pro Jahr" value={formatEUR(annual_cost)} testId="metric-annual" />
            <Metric label="km / Jahr" value={formatNumber(annual_km)} testId="metric-km" />
            <Metric label="Pendlerpauschale" value={formatEUR(pauschale_year)} testId="metric-pauschale" />
          </div>
        </div>
      </motion.div>

      {/* Life time */}
      <div className="grid grid-cols-1 md:grid-cols-2 mt-0 grid-border border-t-0" data-testid="lebenszeit-section">
        <div className="p-8 md:p-12 border-b md:border-b-0 md:border-r border-neutral-200">
          <div className="text-xs uppercase tracking-widest text-neutral-500">Lebenszeit pro Jahr</div>
          <div className="font-display text-6xl md:text-7xl lg:text-8xl mt-3 tnum" data-testid="hours-value">
            {Math.round(annual_hours)} h
          </div>
          <div className="text-neutral-700 mt-3 max-w-sm">
            So viele Stunden verbringst du jedes Jahr im Auto — nur für den Weg zur Arbeit.
          </div>
        </div>
        <div className="p-8 md:p-12 bg-neutral-50">
          <div className="text-xs uppercase tracking-widest text-neutral-500">Das entspricht</div>
          <div className="font-display text-6xl md:text-7xl lg:text-8xl mt-3 tnum" data-testid="days-value">
            {annual_days.toFixed(1)} <span className="text-3xl md:text-4xl font-display">Tage</span>
          </div>
          <div className="text-neutral-700 mt-3 max-w-sm">
            Lebenszeit pro Jahr — Zeit, die nicht für Familie, Sport oder Erholung zur Verfügung steht.
          </div>
        </div>
      </div>

      {/* Comparisons */}
      {comparisons.length > 0 && (
        <div className="mt-12">
          <SectionTitle>Was bedeutet das im echten Leben?</SectionTitle>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-0 grid-border mt-4" data-testid="comparisons">
            {comparisons.map((c, i) => (
              <li
                key={i}
                className="p-6 border-neutral-200 [&:not(:last-child)]:border-b md:[&:nth-child(odd)]:border-r"
                data-testid={`comparison-${i}`}
              >
                <span className="font-mono text-xs text-neutral-500">VERGLEICH {String(i + 1).padStart(2, '0')}</span>
                <p className="mt-2 text-lg leading-snug">{c}</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Recommendations */}
      <div className="mt-12">
        <SectionTitle>Smart-Empfehlungen für dich</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 grid-border mt-4" data-testid="recommendations">
          <AnimatePresence>
            {recommendations.map((r, i) => {
              const Icon = ICON_MAP[r.icon] || CheckCircle;
              return (
                <motion.div
                  key={r.title}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05, duration: 0.3 }}
                  className="p-6 md:p-8 border-neutral-200 [&:not(:last-child)]:border-b md:[&:nth-child(odd)]:border-r md:[&:not(:last-child):not(:nth-last-child(2))]:border-b"
                  data-testid={`rec-${i}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-neutral-900 text-white flex items-center justify-center shrink-0">
                      <Icon size={20} weight="bold" />
                    </div>
                    <div className="flex-1">
                      <div className="font-display text-xl">{r.title}</div>
                      <p className="text-sm text-neutral-700 mt-1 leading-relaxed">{r.detail}</p>
                      <div className="mt-3 flex flex-wrap items-center gap-3">
                        {r.saving_eur > 0 && (
                          <div className="inline-block bg-neutral-100 px-2 py-1 font-mono text-xs">
                            potenziell ~ {formatEUR(r.saving_eur)} / Jahr
                          </div>
                        )}
                        {r.affiliate_url && r.cta_label && (
                          <a
                            href={r.affiliate_url}
                            target="_blank"
                            rel="sponsored noopener noreferrer"
                            data-testid={`rec-cta-${i}`}
                            className="inline-flex items-center gap-1 text-xs uppercase tracking-widest underline underline-offset-4 hover:no-underline"
                          >
                            {r.cta_label} <ArrowRight size={12} weight="bold" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      </div>

      {/* CO2 footprint */}
      <CO2Panel results={results} />

      {/* Benchmark vs DE average */}
      <BenchmarkPanel results={results} />

      {/* Share */}
      <ShareResult results={results} />

      {/* Buy me a coffee */}
      <BuyMeCoffee />
    </div>
  );
}

function Metric({ label, value, testId }) {
  return (
    <div data-testid={testId}>
      <div className="text-xs uppercase tracking-widest text-neutral-500">{label}</div>
      <div className="font-display text-2xl md:text-3xl mt-1 tnum">{value}</div>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div className="flex items-end justify-between">
      <h2 className="font-display text-3xl md:text-4xl tracking-tighter">{children}</h2>
      <div className="hidden md:block h-px flex-1 bg-neutral-200 mx-6 mb-2" />
    </div>
  );
}

function JobSwitchPanel({ baseInputs }) {
  const [currentNet, setCurrentNet] = useState(2800);
  const [newNet, setNewNet] = useState(3200);
  const [newDistance, setNewDistance] = useState(40);
  const [newMinutes, setNewMinutes] = useState(50);

  const result = useMemo(() => {
    return computeJobSwitch({
      currentNet,
      newNet,
      currentInputs: baseInputs,
      newInputs: { ...baseInputs, distance_km: newDistance, minutes_one_way: newMinutes },
    });
  }, [currentNet, newNet, newDistance, newMinutes, baseInputs]);

  const positive = result.real_advantage_month >= 50;

  return (
    <div className="mt-16" id="jobswitch" data-testid="jobswitch-root">
      <div className="flex items-center gap-3 mb-4">
        <Briefcase size={22} weight="bold" />
        <h2 className="font-display text-3xl md:text-4xl tracking-tighter">Jobwechsel-Assistent</h2>
      </div>
      <p className="text-neutral-700 max-w-2xl">
        Vergleiche dein aktuelles Netto mit dem neuen Angebot — abzüglich der zusätzlichen Pendelkosten.
      </p>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 grid-border">
        <Field
          label="Aktuelles Netto / Monat"
          icon={<CurrencyEur size={18} />}
          right="€"
          testId="js-current-net"
          value={currentNet}
          onChange={(v) => setCurrentNet(v)}
          type="number"
        />
        <Field
          label="Neues Netto / Monat"
          icon={<CurrencyEur size={18} />}
          right="€"
          testId="js-new-net"
          value={newNet}
          onChange={(v) => setNewNet(v)}
          type="number"
        />
        <Field
          label="Neue Strecke (einfach)"
          icon={<MapPin size={18} />}
          right="km"
          testId="js-new-distance"
          value={newDistance}
          onChange={(v) => setNewDistance(v)}
          type="number"
        />
        <Field
          label="Neue Fahrzeit (einfach)"
          icon={<Clock size={18} />}
          right="Min."
          testId="js-new-minutes"
          value={newMinutes}
          onChange={(v) => setNewMinutes(v)}
          type="number"
        />
      </div>

      <div
        className="mt-6 p-8 md:p-12 grid-border"
        style={{ backgroundColor: positive ? '#2A9D8F' : '#E63946', color: 'white' }}
        data-testid="js-verdict-box"
      >
        <div className="text-xs uppercase tracking-widest opacity-80">Fazit</div>
        <p className="font-display text-2xl md:text-4xl mt-3 leading-tight" data-testid="js-verdict-text">{result.verdict}</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-8">
          <div>
            <div className="text-xs uppercase tracking-widest opacity-80">Mehr Netto / Monat</div>
            <div className="font-display text-3xl mt-1 tnum" data-testid="js-net-gain">{formatEUR(result.net_gain_month)}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest opacity-80">Mehr Pendelkosten / Monat</div>
            <div className="font-display text-3xl mt-1 tnum" data-testid="js-extra-commute">{formatEUR(result.extra_commute_month)}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest opacity-80">Tatsächlicher Vorteil / Monat</div>
            <div className="font-display text-3xl mt-1 tnum" data-testid="js-real-advantage">{formatEUR(result.real_advantage_month)}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
